/**
 * Console capture ring buffer.
 * Intercepts console.log/warn/error and stores the last N entries
 * so they can be included in error reports.
 *
 * Must be installed before React renders (in index.tsx).
 */

export interface LogEntry {
  ts: number;
  level: 'log' | 'warn' | 'error';
  message: string;
}

const MAX_ENTRIES = 100;
const buffer: LogEntry[] = [];
const IGNORED_CONSOLE_PATTERNS = [
  'cdn.tailwindcss.com should not be used in production',
  'i18next is maintained with support from Locize',
  'Cross-Origin-Opener-Policy policy would block the window.closed call',
  'Cross-Origin-Opener-Policy policy would block the window.close call',
  'net::ERR_BLOCKED_BY_CLIENT',
  'messaging/unsupported-browser',
  'sw.js load failed',
  'installations/app-offline',
  'encountered an error during installation',
];

function stringify(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      try { return JSON.stringify(a); }
      catch { return String(a); }
    })
    .join(' ');
}

let installed = false;

function shouldIgnoreConsoleEntry(args: unknown[]): boolean {
  const message = stringify(args);
  return IGNORED_CONSOLE_PATTERNS.some((pattern) => message.includes(pattern));
}

export function installConsoleCapture(): void {
  if (installed) return;
  installed = true;

  const origLog = console.log.bind(console);
  const origWarn = console.warn.bind(console);
  const origError = console.error.bind(console);

  function push(level: LogEntry['level'], args: unknown[]): void {
    if (buffer.length >= MAX_ENTRIES) buffer.shift();
    buffer.push({ ts: Date.now(), level, message: stringify(args) });
  }

  console.log = (...args: unknown[]) => {
    if (shouldIgnoreConsoleEntry(args)) return;
    push('log', args);
    origLog(...args);
  };
  console.warn = (...args: unknown[]) => {
    if (shouldIgnoreConsoleEntry(args)) return;
    push('warn', args);
    origWarn(...args);
  };
  console.error = (...args: unknown[]) => {
    if (shouldIgnoreConsoleEntry(args)) return;
    push('error', args);
    origError(...args);
  };
}

export function getConsoleLogs(): LogEntry[] {
  return [...buffer];
}
